import React, { useState, useEffect, useRef, useCallback } from 'react';
import { studentsAPI } from '../services/api';
import './StudentImportModal.css';

function StudentImportModal({ 
  isOpen, 
  onClose, 
  sousNiveau,
  allSousNiveaux = [],
  departementId,
  onImportSuccess 
}) {
  // === États principaux ===
  const [currentSousNiveauIndex, setCurrentSousNiveauIndex] = useState(0);
  const [currentSousNiveau, setCurrentSousNiveau] = useState(sousNiveau);
  const [studentCount, setStudentCount] = useState(0);
  const [allStudentIds, setAllStudentIds] = useState([]); // Tous les IDs pour "tout sélectionner"
  
  // === États fichier CSV ===
  const [file, setFile] = useState(null);
  const [encoding, setEncoding] = useState('auto');
  const [dragActive, setDragActive] = useState(false);
  
  // === États analyse ===
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  
  // === États sélection groupes ===
  const [groupesDisponibles, setGroupesDisponibles] = useState([]);
  const [groupesAffectes, setGroupesAffectes] = useState([]);
  const [selectedGroupes, setSelectedGroupes] = useState([]);
  
  // === États vue ===
  const [view, setView] = useState('loading'); // 'loading' | 'list' | 'import' | 'preview'
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [importStats, setImportStats] = useState(null);
  
  // === États liste étudiants ===
  const [etudiants, setEtudiants] = useState([]);
  const [selectedEtudiants, setSelectedEtudiants] = useState([]);
  const [loadingEtudiants, setLoadingEtudiants] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  
  // === États options import ===
  const [importMode, setImportMode] = useState('upsert');
  const [saveMapping, setSaveMapping] = useState(true);
  
  // === États modèles ===
  const [modeles, setModeles] = useState([]);
  const [showModelesSection, setShowModelesSection] = useState(true);
  const [saveModeleName, setSaveModeleName] = useState('');
  const [showSaveModeleInput, setShowSaveModeleInput] = useState(false);
  
  // === États divers ===
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [excludedStudents, setExcludedStudents] = useState([]);
  
  const fileInputRef = useRef(null);
  const ITEMS_PER_PAGE = 20;

  // === Initialisation ===
  useEffect(() => {
    if (isOpen && sousNiveau) {
      const index = allSousNiveaux.findIndex(sn => sn.id === sousNiveau.id);
      setCurrentSousNiveauIndex(index >= 0 ? index : 0);
      setCurrentSousNiveau(sousNiveau);
      initializeView(sousNiveau);
    }
  }, [isOpen, sousNiveau, allSousNiveaux]);

  const initializeView = async (targetSousNiveau) => {
    const snToUse = targetSousNiveau || currentSousNiveau;
    if (!snToUse) return;
    
    setView('loading');
    setError('');
    setSelectedEtudiants([]);
    setCurrentPage(1);
    setSearchTerm('');
    setImportDone(false);
    setImportStats(null);
    setSelectedGroupes([]);
    setAllStudentIds([]);
    
    try {
      // Compter les étudiants
      const countResponse = await studentsAPI.count(snToUse.id);
      const count = countResponse.data.count || 0;
      setStudentCount(count);
      
      // Charger les modèles
      await loadModeles();
      
      if (count > 0) {
        setView('list');
        await loadEtudiants(snToUse.id);
        await loadAllStudentIds(snToUse.id);
      } else {
        setView('import');
      }
    } catch (err) {
      console.error('Erreur initialisation:', err);
      setView('import');
    }
  };

  // === Charger TOUS les IDs des étudiants (pour sélection globale) ===
  const loadAllStudentIds = async (sousNiveauId) => {
    try {
      // Récupérer tous les étudiants sans pagination
      const response = await studentsAPI.getBySousNiveau(sousNiveauId, 1, 10000, '');
      const ids = (response.data.etudiants || []).map(e => e.id);
      setAllStudentIds(ids);
    } catch (err) {
      console.error('Erreur chargement IDs:', err);
    }
  };

  // === Chargement modèles ===
  const loadModeles = async () => {
    try {
      const response = await studentsAPI.getModeles(departementId);
      setModeles(response.data.modeles || []);
    } catch (err) {
      console.error('Erreur chargement modèles:', err);
    }
  };

  // === Chargement étudiants (page courante) ===
  const loadEtudiants = async (sousNiveauId) => {
    const snId = sousNiveauId || currentSousNiveau?.id;
    if (!snId) return;
    
    setLoadingEtudiants(true);
    try {
      const response = await studentsAPI.getBySousNiveau(
        snId,
        currentPage,
        ITEMS_PER_PAGE,
        searchTerm
      );
      setEtudiants(response.data.etudiants || []);
      setTotalPages(response.data.totalPages || 1);
      setStudentCount(response.data.total || 0);
    } catch (err) {
      console.error('Erreur chargement étudiants:', err);
    } finally {
      setLoadingEtudiants(false);
    }
  };

  useEffect(() => {
    if (view === 'list' && currentSousNiveau) {
      loadEtudiants();
    }
  }, [currentPage, searchTerm]);

  // === Chargement des mappings existants ===
  useEffect(() => {
    if (isOpen && departementId && analysis) {
      loadExistingMappings();
    }
  }, [isOpen, departementId, analysis, currentSousNiveau]);

  const loadExistingMappings = async () => {
    try {
      const response = await studentsAPI.getMappings(departementId);
      const mappings = response.data.mappings || [];
      
      const affectes = [];
      const disponibles = [];
      
      analysis.groupes.forEach(groupe => {
        const mapping = mappings.find(m => m.groupe_pronote === groupe.code);
        if (mapping) {
          affectes.push({
            ...groupe,
            sousNiveauId: mapping.sous_niveau_id,
            sousNiveauNom: mapping.sous_niveau_nom
          });
        } else {
          disponibles.push(groupe);
        }
      });
      
      setGroupesAffectes(affectes);
      setGroupesDisponibles(disponibles);
      setSelectedGroupes([]);
    } catch (err) {
      console.error('Erreur chargement mappings:', err);
      if (analysis) {
        setGroupesDisponibles(analysis.groupes);
        setGroupesAffectes([]);
      }
    }
  };

  // === Sélection étudiants ===
  const toggleEtudiant = (id) => {
    setSelectedEtudiants(prev => {
      if (prev.includes(id)) {
        return prev.filter(e => e !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Sélectionner/Désélectionner TOUS les étudiants (pas juste la page)
  const toggleAllEtudiants = () => {
    if (selectedEtudiants.length === allStudentIds.length && allStudentIds.length > 0) {
      setSelectedEtudiants([]);
    } else {
      setSelectedEtudiants([...allStudentIds]);
    }
  };

  const isAllSelected = allStudentIds.length > 0 && selectedEtudiants.length === allStudentIds.length;

  // === Suppression étudiants ===
  const handleDeleteSelected = () => {
    if (selectedEtudiants.length === 0) return;
    
    const isAll = selectedEtudiants.length === studentCount;
    setConfirmAction({
      type: 'delete-students',
      message: isAll 
        ? `Supprimer TOUS les ${studentCount} étudiants de "${currentSousNiveau.nom}" ?`
        : `Supprimer ${selectedEtudiants.length} étudiant(s) sélectionné(s) ?`,
      ids: selectedEtudiants,
      isAll
    });
  };

  const confirmDeleteStudents = async () => {
    if (!confirmAction || confirmAction.type !== 'delete-students') return;
    
    try {
      await studentsAPI.batchDelete(confirmAction.ids);
      
      setSelectedEtudiants([]);
      setConfirmAction(null);
      
      // Recharger et vérifier si reste des étudiants
      const countResponse = await studentsAPI.count(currentSousNiveau.id);
      const newCount = countResponse.data.count || 0;
      setStudentCount(newCount);
      
      if (newCount === 0) {
        setAllStudentIds([]);
        setView('import');
      } else {
        setCurrentPage(1);
        await loadEtudiants();
        await loadAllStudentIds(currentSousNiveau.id);
      }
      
      if (onImportSuccess) {
        onImportSuccess(-confirmAction.ids.length);
      }
      
    } catch (err) {
      setError('Erreur lors de la suppression: ' + (err.response?.data?.error || err.message));
    }
  };

  // === Drag & Drop ===
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      handleFileSelected(droppedFile);
    }
  }, [encoding, departementId]);

  const handleFileSelected = async (selectedFile) => {
    setFile(selectedFile);
    setError('');
    setAnalysis(null);
    setSelectedGroupes([]);
    
    await analyzeFile(selectedFile);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelected(e.target.files[0]);
    }
  };

  // === Analyse CSV ===
  const analyzeFile = async (fileToAnalyze) => {
    setAnalyzing(true);
    setError('');
    
    try {
      const response = await studentsAPI.analyzeCSV(
        fileToAnalyze,
        departementId,
        encoding === 'auto' ? null : encoding
      );
      
      setAnalysis(response.data.preview);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'analyse du fichier');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (file && encoding) {
      analyzeFile(file);
    }
  }, [encoding]);

  // === Sélection/Désélection groupe ===
  const toggleGroupe = (groupeCode) => {
    setSelectedGroupes(prev => {
      if (prev.includes(groupeCode)) {
        return prev.filter(g => g !== groupeCode);
      } else {
        return [...prev, groupeCode];
      }
    });
  };

  // === Désaffectation groupe (simple clic) ===
  const handleDesaffectClick = (groupe) => {
    setConfirmAction({
      type: 'desaffect',
      groupe,
      message: `Désaffecter le groupe "${groupe.code}" ?\n\nLes ${groupe.count} étudiants seront retirés de "${groupe.sousNiveauNom}".`
    });
  };

  const confirmDesaffectGroupe = async () => {
    if (!confirmAction || confirmAction.type !== 'desaffect') return;
    
    const groupe = confirmAction.groupe;
    
    try {
      await studentsAPI.desaffectGroupe(departementId, groupe.code);
      
      setGroupesAffectes(prev => prev.filter(g => g.code !== groupe.code));
      setGroupesDisponibles(prev => [...prev, { 
        code: groupe.code, 
        count: groupe.count 
      }]);
      
      setConfirmAction(null);
      
      if (onImportSuccess) {
        onImportSuccess(-groupe.count);
      }
    } catch (err) {
      setError('Erreur lors de la désaffectation: ' + (err.response?.data?.error || err.message));
    }
  };

  // === Modèles d'affectation ===
  const applyModele = (modele) => {
    if (!analysis) return;
    
    const mappings = modele.mappings;
    const toSelect = [];
    
    analysis.groupes.forEach(groupe => {
      if (mappings[groupe.code] === currentSousNiveau.id) {
        toSelect.push(groupe.code);
      }
    });
    
    setSelectedGroupes(toSelect);
  };

  const handleSaveModele = async () => {
    if (!saveModeleName.trim()) {
      setError('Veuillez entrer un nom pour le modèle');
      return;
    }
    
    try {
      const response = await studentsAPI.getMappings(departementId);
      const existingMappings = response.data.mappings || [];
      
      const mappingsObj = {};
      existingMappings.forEach(m => {
        mappingsObj[m.groupe_pronote] = m.sous_niveau_id;
      });
      
      await studentsAPI.saveModele({
        departementId,
        nom: saveModeleName,
        mappings: mappingsObj
      });
      
      setSaveModeleName('');
      setShowSaveModeleInput(false);
      await loadModeles();
      
    } catch (err) {
      setError('Erreur lors de la sauvegarde: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteModele = (modele) => {
    setConfirmAction({
      type: 'delete-modele',
      modele,
      message: `Supprimer le modèle "${modele.nom}" ?`
    });
  };

  const confirmDeleteModele = async () => {
    if (!confirmAction || confirmAction.type !== 'delete-modele') return;
    
    try {
      await studentsAPI.deleteModele(confirmAction.modele.id);
      setConfirmAction(null);
      await loadModeles();
    } catch (err) {
      setError('Erreur lors de la suppression: ' + (err.response?.data?.error || err.message));
    }
  };

  // === Étudiants sélectionnés pour import ===
  const getSelectedStudents = () => {
    if (!analysis || !analysis.allStudents) return [];
    
    return analysis.allStudents.filter(student => 
      selectedGroupes.includes(student.groupe_pronote) &&
      !excludedStudents.includes(student.uid_pronote)
    );
  };

  const getPreviewStudents = () => {
    const preview = [];
    selectedGroupes.forEach(groupeCode => {
      const student = analysis?.allStudents?.find(s => s.groupe_pronote === groupeCode);
      if (student && !excludedStudents.includes(student.uid_pronote)) {
        preview.push(student);
      }
    });
    return preview;
  };

  const getTotalSelected = () => {
    return getSelectedStudents().length;
  };

  const getTotalRemaining = () => {
    const totalDisponibles = groupesDisponibles.reduce((acc, g) => acc + g.count, 0);
    return totalDisponibles - getTotalSelected();
  };

  const excludeStudent = (uid) => {
    setExcludedStudents(prev => [...prev, uid]);
  };

  // === Navigation niveaux ===
  const goToPreviousLevel = async () => {
    if (currentSousNiveauIndex > 0) {
      const newIndex = currentSousNiveauIndex - 1;
      const newSousNiveau = allSousNiveaux[newIndex];
      setCurrentSousNiveauIndex(newIndex);
      setCurrentSousNiveau(newSousNiveau);
      await initializeView(newSousNiveau);
    }
  };

  const goToNextLevel = async () => {
    if (currentSousNiveauIndex < allSousNiveaux.length - 1) {
      const newIndex = currentSousNiveauIndex + 1;
      const newSousNiveau = allSousNiveaux[newIndex];
      setCurrentSousNiveauIndex(newIndex);
      setCurrentSousNiveau(newSousNiveau);
      await initializeView(newSousNiveau);
    }
  };

  // === Import ===
  const handleImport = async () => {
    if (!file || selectedGroupes.length === 0) {
      setError('Sélectionnez au moins un groupe');
      return;
    }
    
    setImporting(true);
    setError('');
    
    try {
      const response = await studentsAPI.import(file, {
        departementId,
        sousNiveauId: currentSousNiveau.id,
        filtreGroupes: selectedGroupes,
        excludedUids: excludedStudents,
        mode: importMode,
        saveMapping,
        encoding: encoding === 'auto' ? null : encoding
      });
      
      setImportStats(response.data);
      setImportDone(true);
      
      const imported = groupesDisponibles.filter(g => selectedGroupes.includes(g.code));
      setGroupesAffectes(prev => [...prev, ...imported.map(g => ({
        ...g,
        sousNiveauId: currentSousNiveau.id,
        sousNiveauNom: currentSousNiveau.nom
      }))]);
      setGroupesDisponibles(prev => prev.filter(g => !selectedGroupes.includes(g.code)));
      setSelectedGroupes([]);
      
      const countResponse = await studentsAPI.count(currentSousNiveau.id);
      setStudentCount(countResponse.data.count || 0);
      
      if (onImportSuccess) {
        onImportSuccess(response.data.imported + response.data.updated);
      }
      
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  // === Navigation entre vues ===
  const goToImportView = () => {
    setView('import');
    setImportDone(false);
    setImportStats(null);
    setSelectedGroupes([]);
    setExcludedStudents([]);
  };

  const goToListView = async () => {
    setView('list');
    setCurrentPage(1);
    await loadEtudiants();
    await loadAllStudentIds(currentSousNiveau.id);
  };

  // === Pagination pour prévisualisation ===
  const getFilteredStudentsForPreview = () => {
    let students = getSelectedStudents();
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      students = students.filter(s => 
        s.nom?.toLowerCase().includes(term) ||
        s.prenom?.toLowerCase().includes(term) ||
        s.ine?.toLowerCase().includes(term)
      );
    }
    
    return students;
  };

  const getPaginatedStudentsForPreview = () => {
    const filtered = getFilteredStudentsForPreview();
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  };

  const getTotalPagesForPreview = () => {
    return Math.ceil(getFilteredStudentsForPreview().length / ITEMS_PER_PAGE);
  };

  // === Rendu ===
  if (!isOpen) return null;

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    
    switch (confirmAction.type) {
      case 'delete-students':
        confirmDeleteStudents();
        break;
      case 'desaffect':
        confirmDesaffectGroupe();
        break;
      case 'delete-modele':
        confirmDeleteModele();
        break;
      default:
        setConfirmAction(null);
    }
  };

  const getDefaultModeleName = () => {
    const date = new Date();
    const mois = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return `Import ${mois}`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content student-import-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="modal-header">
          <h2>👥 {view === 'list' ? 'Étudiants' : 'Import étudiants'}</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Barre de navigation niveaux */}
        <div className="level-navigation">
          <button 
            className="btn btn-nav"
            onClick={goToPreviousLevel}
            disabled={currentSousNiveauIndex === 0}
          >
            ◀ Précédent
          </button>
          <span className="level-indicator">
            {currentSousNiveau?.nom} 
            <span className="level-count">({studentCount} étudiant{studentCount > 1 ? 's' : ''})</span>
            <span className="level-position">({currentSousNiveauIndex + 1}/{allSousNiveaux.length})</span>
          </span>
          <button 
            className="btn btn-nav"
            onClick={goToNextLevel}
            disabled={currentSousNiveauIndex === allSousNiveaux.length - 1}
          >
            Suivant ▶
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          
          {/* === VUE LOADING === */}
          {view === 'loading' && (
            <div className="loading-view">
              <div className="loading-spinner">⏳</div>
              <p>Chargement...</p>
            </div>
          )}

          {/* === VUE LISTE ÉTUDIANTS === */}
          {view === 'list' && (
            <div className="list-view">
              
              {/* Recherche */}
              <div className="search-bar">
                <input 
                  type="text" 
                  placeholder="🔍 Rechercher (nom, prénom, INE)..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Liste étudiants */}
              <div className="students-list">
                {loadingEtudiants ? (
                  <div className="loading-view">
                    <div className="loading-spinner">⏳</div>
                  </div>
                ) : etudiants.length === 0 ? (
                  <div className="empty-message">Aucun étudiant trouvé</div>
                ) : (
                  etudiants.map(etudiant => (
                    <div 
                      key={etudiant.id}
                      className={`student-item ${selectedEtudiants.includes(etudiant.id) ? 'selected' : ''}`}
                      onClick={() => toggleEtudiant(etudiant.id)}
                    >
                      <span className="student-name">{etudiant.nom} {etudiant.prenom}</span>
                      <span className="student-groupe">{etudiant.groupe_pronote || '-'}</span>
                      {selectedEtudiants.includes(etudiant.id) && (
                        <span className="student-check">✅</span>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ◀
                  </button>
                  <span>Page {currentPage} / {totalPages}</span>
                  <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    ▶
                  </button>
                </div>
              )}

              {/* Sélection tout */}
              <div 
                className={`select-all-item ${isAllSelected ? 'selected' : ''}`}
                onClick={toggleAllEtudiants}
              >
                <span>
                  {isAllSelected 
                    ? `Tout désélectionner (${allStudentIds.length})` 
                    : `Tout sélectionner (${allStudentIds.length})`
                  }
                </span>
                {isAllSelected && <span className="student-check">✅</span>}
              </div>

              {/* Info sélection */}
              {selectedEtudiants.length > 0 && (
                <div className="selection-info">
                  {selectedEtudiants.length} étudiant(s) sélectionné(s)
                </div>
              )}

              {/* Erreur */}
              {error && <div className="alert alert-error">{error}</div>}

              {/* Boutons */}
              <div className="modal-actions">
                <button 
                  className="btn btn-danger"
                  onClick={handleDeleteSelected}
                  disabled={selectedEtudiants.length === 0}
                >
                  🗑️ Supprimer ({selectedEtudiants.length})
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={goToImportView}
                >
                  📂 Importer CSV
                </button>
                <button className="btn btn-secondary" onClick={onClose}>
                  Fermer
                </button>
              </div>
            </div>
          )}

          {/* === VUE IMPORT === */}
          {view === 'import' && (
            <div className="import-view">
              
              {/* Section Modèles */}
              {modeles.length > 0 && (
                <div className={`modeles-section ${showModelesSection ? '' : 'collapsed'}`}>
                  <div 
                    className="modeles-header"
                    onClick={() => setShowModelesSection(!showModelesSection)}
                  >
                    <span>💾 Modèles d'affectation ({modeles.length})</span>
                    <span className="toggle-icon">{showModelesSection ? '▼' : '▶'}</span>
                  </div>
                  {showModelesSection && (
                    <div className="modeles-content">
                      {modeles.map(modele => (
                        <div key={modele.id} className="modele-item">
                          <div className="modele-info">
                            <span className="modele-name">📋 {modele.nom}</span>
                            <span className="modele-count">
                              ({Object.keys(modele.mappings || {}).length} groupes)
                            </span>
                          </div>
                          <div className="modele-actions">
                            <button 
                              className="btn btn-small btn-primary"
                              onClick={() => applyModele(modele)}
                              disabled={!analysis}
                              title={analysis ? 'Appliquer ce modèle' : 'Chargez d\'abord un CSV'}
                            >
                              Appliquer
                            </button>
                            <button 
                              className="btn btn-small btn-danger"
                              onClick={() => handleDeleteModele(modele)}
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Bouton sauvegarder modèle */}
              {!showSaveModeleInput ? (
                <button 
                  className="btn btn-small btn-secondary save-modele-btn"
                  onClick={() => {
                    setSaveModeleName(getDefaultModeleName());
                    setShowSaveModeleInput(true);
                  }}
                >
                  ➕ Sauvegarder modèle actuel
                </button>
              ) : (
                <div className="save-modele-form">
                  <input 
                    type="text"
                    value={saveModeleName}
                    onChange={(e) => setSaveModeleName(e.target.value)}
                    placeholder="Nom du modèle..."
                  />
                  <button className="btn btn-small btn-success" onClick={handleSaveModele}>
                    Sauvegarder
                  </button>
                  <button className="btn btn-small btn-secondary" onClick={() => setShowSaveModeleInput(false)}>
                    Annuler
                  </button>
                </div>
              )}

              {/* Zone Upload */}
              <div 
                className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input 
                  type="file" 
                  ref={fileInputRef}
                  accept=".csv,.txt" 
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
                {analyzing ? (
                  <div className="upload-status">⏳ Analyse en cours...</div>
                ) : file ? (
                  <div className="upload-status success">
                    ✅ {file.name} ({(file.size / 1024).toFixed(1)} Ko)
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    📁 Glissez votre fichier CSV ici<br/>
                    <span className="upload-hint">ou cliquez pour parcourir</span>
                  </div>
                )}
              </div>

              {/* Encodage */}
              <div className="encoding-selector">
                <label>Encodage :</label>
                <select value={encoding} onChange={(e) => setEncoding(e.target.value)}>
                  <option value="auto">Détection auto</option>
                  <option value="UTF-8">UTF-8</option>
                  <option value="ISO-8859-1">ISO-8859-1 (Latin-1)</option>
                  <option value="Windows-1252">Windows-1252</option>
                </select>
              </div>

              {/* Contenu après analyse */}
              {analysis && (
                <div className="analysis-content">
                  <div className="groups-container">
                    
                    {/* Groupes disponibles */}
                    <div className="groups-section">
                      <h3>📋 Groupes à affecter à "{currentSousNiveau?.nom}"</h3>
                      <div className="groups-list">
                        {groupesDisponibles.length === 0 ? (
                          <div className="empty-message">Tous les groupes sont déjà affectés</div>
                        ) : (
                          groupesDisponibles.map(groupe => (
                            <div 
                              key={groupe.code}
                              className={`group-item ${selectedGroupes.includes(groupe.code) ? 'selected' : ''}`}
                              onClick={() => toggleGroupe(groupe.code)}
                            >
                              <span className="group-name">{groupe.code}</span>
                              <span className="group-count">({groupe.count})</span>
                              {selectedGroupes.includes(groupe.code) && (
                                <span className="group-check">✅</span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Prévisualisation mini */}
                    <div className="preview-section">
                      <h3>👁️ Aperçu ({getTotalSelected()} étudiants)</h3>
                      <div className="preview-list">
                        {getPreviewStudents().length === 0 ? (
                          <div className="empty-message">Sélectionnez des groupes</div>
                        ) : (
                          getPreviewStudents().map(student => (
                            <div key={student.uid_pronote} className="preview-item">
                              <span className="student-name">{student.nom} {student.prenom}</span>
                              <span className="student-groupe">{student.groupe_pronote}</span>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="preview-stats">
                        📊 {getTotalSelected()} sélectionnés • {getTotalRemaining()} restants à affecter
                      </div>
                    </div>
                  </div>

                  {/* Groupes déjà affectés */}
                  {groupesAffectes.length > 0 && (
                    <div className="affected-section">
                      <h3>🔒 Déjà affectés</h3>
                      <div className="groups-list affected">
                        {groupesAffectes.map(groupe => (
                          <div key={groupe.code} className="group-item affected">
                            <span className="group-name">{groupe.code}</span>
                            <span className="group-count">({groupe.count})</span>
                            <span className="group-target">→ {groupe.sousNiveauNom}</span>
                            <span 
                              className="group-lock" 
                              onClick={() => handleDesaffectClick(groupe)}
                              title="Clic pour désaffecter"
                            >
                              🔒
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Erreur */}
              {error && <div className="alert alert-error">{error}</div>}

				{/* Boutons */}
				<div className="modal-actions">
				  {studentCount > 0 && (
					<button className="btn btn-secondary" onClick={goToListView}>
					  ← Retour à la liste
					</button>
				  )}
				  <button className="btn btn-secondary" onClick={onClose}>
					Fermer
				  </button>
				  <button 
					className="btn btn-primary"
					onClick={() => setView('preview')}
					disabled={selectedGroupes.length === 0}
				  >
					Pré-visualiser ({getTotalSelected()})
				  </button>
				  <button 
					className="btn btn-success"
					onClick={handleImport}
					disabled={importing || selectedGroupes.length === 0}
				  >
					{importing ? '⏳ Import...' : `Importer (${getTotalSelected()})`}
				  </button>
				</div>
            </div>
          )}

          {/* === VUE PRÉVISUALISATION COMPLÈTE === */}
          {view === 'preview' && (
            <div className="preview-view">

              {/* Recherche */}
              <div className="search-bar">
                <input 
                  type="text" 
                  placeholder="🔍 Rechercher (nom, prénom, INE)..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Liste complète */}
              <div className="full-list">
                <table className="students-table">
                  <thead>
                    <tr>
                      <th>Nom Prénom</th>
                      <th>Groupe</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedStudentsForPreview().map(student => (
                      <tr key={student.uid_pronote}>
                        <td>{student.nom} {student.prenom}</td>
                        <td>{student.groupe_pronote}</td>
                        <td>
                          <button 
                            className="btn-icon btn-danger"
                            onClick={() => excludeStudent(student.uid_pronote)}
                            title="Retirer de l'import"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {getTotalPagesForPreview() > 1 && (
                  <div className="pagination">
                    <button 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      ◀
                    </button>
                    <span>Page {currentPage} / {getTotalPagesForPreview()}</span>
                    <button 
                      onClick={() => setCurrentPage(p => Math.min(getTotalPagesForPreview(), p + 1))}
                      disabled={currentPage === getTotalPagesForPreview()}
                    >
                      ▶
                    </button>
                  </div>
                )}
              </div>

              {/* Options import */}
              <div className="import-options">
                <div className="option-group">
                  <label>Mode :</label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      value="upsert" 
                      checked={importMode === 'upsert'}
                      onChange={(e) => setImportMode(e.target.value)}
                    />
                    Fusionner
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      value="replace" 
                      checked={importMode === 'replace'}
                      onChange={(e) => setImportMode(e.target.value)}
                    />
                    Remplacer tout
                  </label>
                </div>
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={saveMapping}
                    onChange={(e) => setSaveMapping(e.target.checked)}
                  />
                  Mémoriser l'affectation
                </label>
              </div>

              {/* Message succès */}
              {importDone && importStats && (
                <div className="alert alert-success">
                  ✅ Import terminé ! {importStats.imported} importés, {importStats.updated} mis à jour
                  {importStats.errors?.length > 0 && (
                    <span> • {importStats.errors.length} erreur(s)</span>
                  )}
                </div>
              )}

              {/* Erreur */}
              {error && <div className="alert alert-error">{error}</div>}

              {/* Boutons */}
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setView('import')}>
                  Retour
                </button>
                <button 
                  className="btn btn-success"
                  onClick={handleImport}
                  disabled={importing || importDone || getTotalSelected() === 0}
                >
                  {importing ? '⏳ Import...' : importDone ? '✅ Importé' : `Importer (${getTotalSelected()})`}
                </button>
                {importDone && (
                  <button className="btn btn-secondary" onClick={onClose}>
                    Fermer
                  </button>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Modal de confirmation */}
        {confirmAction && (
          <div className="confirm-overlay">
            <div className="confirm-dialog">
              <h3>⚠️ Confirmation</h3>
              <p style={{ whiteSpace: 'pre-line' }}>{confirmAction.message}</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>
                  Annuler
                </button>
                <button className="btn btn-danger" onClick={handleConfirmAction}>
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default StudentImportModal;
